import {computed, InjectionToken, Signal, signal} from '@angular/core';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {
    AbstractControl,
    FormControl,
    FormGroup,
    NonNullableFormBuilder,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import {AppValidators} from '@brunata-angular/util';
import {DateTime} from 'luxon';
import {map, startWith, switchMap} from 'rxjs';
import {
    ExportScheduleValue,
    maxSavedExportPathLength,
    maxScheduleDayOfMonth,
    maxScheduleInterval,
    minScheduleInterval,
    SavedExport,
    SavedExportDeliveryChannel,
    SavedExportRecurrence,
    SavedExportSchedulePeriod,
    SavedExportScheduleUnit,
    SavedExportTestRequest,
    SavedExportWeekday,
} from '../../saved-exports/saved-export.model';

// The one form shape every saved-export editor shares (quick wizard, customized wizard, edit
// modal). The shared name/schedule steps inject it through EXPORT_FORM; each context provides its
// own form service's group. name is nullable because the customized wizard uses null for "no name
// yet" (default-name prefill, AGI-18951).
// customSchedule is the checkbox only: it decides whether the unit below is sent at all, so an
// export that is switched back to a recurrence keeps the values the user typed.
export type ExportScheduleForm = FormGroup<{
    recurrence: FormControl<SavedExportRecurrence>;
    startDate: FormControl<DateTime | null>;
    recipients: FormControl<string[]>;
    deliveryChannel: FormControl<SavedExportDeliveryChannel>;
    sftpConnectionId: FormControl<string | null>;
    sftpSubfolder: FormControl<string | null>;
    fileNamePattern: FormControl<string | null>;
    customSchedule: FormControl<boolean>;
    scheduleUnit: FormControl<SavedExportScheduleUnit>;
    scheduleInterval: FormControl<number>;
    scheduleWeekdays: FormControl<SavedExportWeekday[]>;
    scheduleDayOfMonth: FormControl<number>;
    schedulePeriod: FormControl<SavedExportSchedulePeriod>;
}>;

export type ExportForm = FormGroup<{
    name: FormControl<string | null>;
    timeZone: FormControl<string>;
    schedule: ExportScheduleForm;
}>;

export const EXPORT_FORM = new InjectionToken<ExportForm>('EXPORT_FORM');

function requiredWhenChannelIs(channel: SavedExportDeliveryChannel): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null =>
        control.parent?.get('deliveryChannel')?.value === channel
            ? Validators.required(control)
            : null;
}

function whenCustomScheduleIs(
    unit: SavedExportScheduleUnit | null,
    ...validators: ValidatorFn[]
): ValidatorFn {
    const combined = Validators.compose([Validators.required, ...validators]);
    return (control: AbstractControl): ValidationErrors | null => {
        const group = control.parent;
        if (group?.get('customSchedule')?.value !== true) {
            return null;
        }
        return unit === null || group.get('scheduleUnit')?.value === unit
            ? (combined?.(control) ?? null)
            : null;
    };
}

function unlessEndedMonths(validator: ValidatorFn): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null =>
        control.parent?.get('schedulePeriod')?.value === 'ENDED_MONTHS' ? null : validator(control);
}

/** Every control whose requirement depends on the chosen channel or unit. */
export const conditionalScheduleControls = [
    'recipients',
    'sftpConnectionId',
    'scheduleInterval',
    'scheduleWeekdays',
    'scheduleDayOfMonth',
] as const;

/**
 * The delivery controls are required only for the channel and unit they belong to, so switching
 * tab or unit does not leave the user with an error on a field the screen no longer shows. The
 * schedule step revalidates them whenever that switch happens.
 */
export function exportScheduleGroup(
    formBuilder: NonNullableFormBuilder,
    defaults: ExportScheduleValue,
): ExportScheduleForm {
    const group = formBuilder.group({
        recurrence: formBuilder.control<SavedExportRecurrence>(defaults.recurrence),
        startDate: formBuilder.control<DateTime | null>(defaults.startDate, [Validators.required]),
        recipients: formBuilder.control<string[]>(defaults.recipients, [
            requiredWhenChannelIs('EMAIL'),
        ]),
        deliveryChannel: formBuilder.control<SavedExportDeliveryChannel>(defaults.deliveryChannel),
        sftpConnectionId: formBuilder.control<string | null>(defaults.sftpConnectionId, [
            requiredWhenChannelIs('SFTP'),
        ]),
        sftpSubfolder: formBuilder.control<string | null>(defaults.sftpSubfolder, [
            Validators.maxLength(maxSavedExportPathLength),
        ]),
        fileNamePattern: formBuilder.control<string | null>(defaults.fileNamePattern, [
            Validators.maxLength(maxSavedExportPathLength),
        ]),
        customSchedule: formBuilder.control<boolean>(defaults.customSchedule),
        scheduleUnit: formBuilder.control<SavedExportScheduleUnit>(defaults.scheduleUnit),
        scheduleInterval: formBuilder.control<number>(defaults.scheduleInterval, [
            whenCustomScheduleIs(
                null,
                AppValidators.integer,
                Validators.min(minScheduleInterval),
                Validators.max(maxScheduleInterval),
            ),
        ]),
        scheduleWeekdays: formBuilder.control<SavedExportWeekday[]>(defaults.scheduleWeekdays, [
            whenCustomScheduleIs('WEEKS'),
        ]),
        scheduleDayOfMonth: formBuilder.control<number>(defaults.scheduleDayOfMonth, [
            unlessEndedMonths(
                whenCustomScheduleIs(
                    'MONTHS',
                    AppValidators.integer,
                    Validators.min(1),
                    Validators.max(maxScheduleDayOfMonth),
                ),
            ),
        ]),
        schedulePeriod: formBuilder.control<SavedExportSchedulePeriod>(defaults.schedulePeriod),
    });
    // FormControl validates in its constructor, before FormGroup sets parent; run once with siblings.
    conditionalScheduleControls.forEach(name => group.controls[name].updateValueAndValidity());
    return group;
}

export function scheduleFormValue(
    schedule: Signal<ExportScheduleForm>,
): Signal<ExportScheduleValue | undefined> {
    return toSignal(
        toObservable(schedule).pipe(
            switchMap(form =>
                form.valueChanges.pipe(
                    startWith(undefined),
                    map(() => form.getRawValue()),
                ),
            ),
        ),
    );
}

export function defaultExportName(translate: () => string): Signal<string> {
    return computed(() => {
        const translated = translate();
        return translated.startsWith('[') ? '' : translated;
    });
}

export type ExportTestSource = {
    saveDisabled?: Signal<boolean>;
    buildTestRequest(): SavedExportTestRequest;
    validateForTest?(): boolean;
};

export const EXPORT_TEST_SOURCE = new InjectionToken<ExportTestSource>('EXPORT_TEST_SOURCE');

export type ExportTestAction = {
    readonly disabled: Signal<boolean>;
    readonly running: Signal<boolean>;
    readonly cooldownSeconds: Signal<number>;
    run(): void;
};

export const EXPORT_TEST_ACTION = new InjectionToken<ExportTestAction>('EXPORT_TEST_ACTION');

export type SftpConnectionRemoval = {
    readonly removed: Signal<boolean>;
    load(savedExport: SavedExport): void;
    clear(): void;
};

export function sftpConnectionRemoval(schedule: ExportScheduleForm): SftpConnectionRemoval {
    const removed = signal(false);
    return {
        removed: removed.asReadonly(),
        load: (savedExport): void => {
            removed.set(savedExport.sftpConnectionMissing);
            if (savedExport.sftpConnectionMissing) {
                schedule.controls.sftpConnectionId.markAsTouched();
            }
        },
        clear: (): void => removed.set(false),
    };
}
